/**
 * Feedback service for submitting thumbs up/down feedback on reasoning chains
 */
import { fetchWithDefaults } from '@/lib/http/client';

export type FeedbackRating = 'positive' | 'negative';

export type FeedbackCategory =
  | 'hallucination'
  | 'wrong_entity'
  | 'wrong_value'
  | 'incomplete'
  | 'unhelpful'
  | 'wrong_tone'
  | 'other';

export interface StructuredFeedbackData {
  rating: FeedbackRating;
  category?: FeedbackCategory;
  comment?: string;
  flagged_step_id?: number;
  correction_data?: Record<string, unknown>;
}

export interface FeedbackResponse {
  message: string;
  created?: boolean;
}

/**
 * Submit feedback (thumbs up/down) for a reasoning chain.
 * Accepts either a simple rating string or structured feedback data.
 */
export async function submitChainFeedback(
  orgId: number,
  conversationId: number,
  chainId: number,
  feedback: FeedbackRating | StructuredFeedbackData
): Promise<FeedbackResponse> {
  // Normalize to object format for API
  const body = typeof feedback === 'string' ? { rating: feedback } : feedback;

  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/chains/${chainId}/feedback/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    // Parse error response body for better debugging
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.detail ||
      errorData.error ||
      errorData.message ||
      'Unknown error';
    throw new Error(
      `Failed to submit feedback (${response.status}): ${errorMessage}`
    );
  }

  return response.json();
}

/**
 * Remove feedback from a reasoning chain (toggle off)
 */
export async function removeChainFeedback(
  orgId: number,
  conversationId: number,
  chainId: number
): Promise<FeedbackResponse> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/chains/${chainId}/feedback/`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  // 404 is acceptable - means feedback was already removed
  if (!response.ok && response.status !== 404) {
    throw new Error('Failed to remove feedback');
  }

  if (
    response.status === 204 ||
    response.headers.get('content-length') === '0'
  ) {
    return { message: 'Feedback removed' };
  }

  return response.json();
}
